import { useEffect, useRef } from 'react';
import { useLeafletContext } from '@react-leaflet/core';
import * as L from 'leaflet';

interface IndiaBorderMaskProps {
  indiaGeoJSON: any;
}

/**
 * IndiaBorderMask creates an inverted mask that hides everything outside India's borders.
 * This is achieved by creating a large rectangle covering the entire world,
 * with India's shape cut out as holes.
 */
function IndiaBorderMask({ indiaGeoJSON }: IndiaBorderMaskProps) {
  const context = useLeafletContext();
  const layerRef = useRef<L.LayerGroup>(new L.LayerGroup());

  useEffect(() => {
    const { map } = context;
    const layer = layerRef.current;
    // Create dedicated panes for mask and border
    const maskPaneName = 'india-mask-pane';
    const borderPaneName = 'india-border-pane';
    if (!map.getPane(maskPaneName)) {
      map.createPane(maskPaneName);
    }
    if (!map.getPane(borderPaneName)) {
      map.createPane(borderPaneName);
    }
    const maskPane = map.getPane(maskPaneName);
    const borderPane = map.getPane(borderPaneName);
    if (maskPane) {
      maskPane.style.zIndex = '500';
      maskPane.style.pointerEvents = 'none';
    }
    if (borderPane) {
      borderPane.style.zIndex = '600';
      borderPane.style.pointerEvents = 'none';
    }
    
    // Add layer to map
    map.addLayer(layer);
    
    return () => {
      map.removeLayer(layer);
    };
  }, [context]);

  useEffect(() => {
    const layer = layerRef.current;
    layer.clearLayers();

    if (!indiaGeoJSON) return;

    try {
      // Create world bounding box (covers entire world)
      const worldBounds = [
        [-90, -180], [-90, 180], [90, 180], [90, -180], [-90, -180]
      ];

      // Extract India's coordinates - handle both Polygon and MultiPolygon
      const indiaHoles: L.LatLng[][] = [];

      const geometries: any[] = [];
      if (indiaGeoJSON.type === 'FeatureCollection' && Array.isArray(indiaGeoJSON.features)) {
        for (const f of indiaGeoJSON.features) {
          if (f && f.geometry) geometries.push(f.geometry);
        }
      } else if (indiaGeoJSON.type === 'Feature' && indiaGeoJSON.geometry) {
        geometries.push(indiaGeoJSON.geometry);
      } else if (indiaGeoJSON.type === 'Polygon' || indiaGeoJSON.type === 'MultiPolygon') {
        geometries.push(indiaGeoJSON);
      }

      for (const geom of geometries) {
        if (geom.type === 'Polygon') {
          (geom.coordinates as number[][][]).forEach((ring: number[][]) => {
            const hole = ring.map(([lon, lat]) => new L.LatLng(lat, lon));
            indiaHoles.push(hole);
          });
        } else if (geom.type === 'MultiPolygon') {
          (geom.coordinates as number[][][][]).forEach((polygon: number[][][]) => {
            polygon.forEach((ring: number[][]) => {
              const hole = ring.map(([lon, lat]) => new L.LatLng(lat, lon));
              indiaHoles.push(hole);
            });
          });
        }
      }

      // Create the main world polygon with India cut out as holes
      const worldPolygon = worldBounds.map(([lat, lon]) => new L.LatLng(lat, lon));
      
      // Create a polygon that covers the world but has India as holes
      const maskPolygon = L.polygon([worldPolygon, ...indiaHoles], {
        color: '#ffffff',
        fillColor: '#ffffff',
        fillOpacity: 1.0, // White-out outside India
        weight: 0,
        stroke: false,
        interactive: false, // Don't interfere with map interactions
        pane: 'india-mask-pane'
      });

      // Add the mask to the layer
      layer.addLayer(maskPolygon);

      // Add a halo (white stroke) for better contrast
      const haloPolygon = L.geoJSON(indiaGeoJSON, {
        style: {
          color: '#ffffff',
          weight: 5,
          opacity: 0.9,
          fillOpacity: 0,
          fillColor: 'transparent'
        },
        interactive: false,
        pane: 'india-border-pane'
      });

      // Add India border outline on top of the halo
      const borderPolygon = L.geoJSON(indiaGeoJSON, {
        style: {
          color: '#941C30',
          weight: 3,
          opacity: 1,
          fillOpacity: 0,
          fillColor: 'transparent'
        },
        interactive: false,
        pane: 'india-border-pane'
      });
      if (haloPolygon) layer.addLayer(haloPolygon);
      if (borderPolygon) layer.addLayer(borderPolygon);

      console.log('✅ India border mask applied successfully');
      
    } catch (error) {
      const details = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      console.error('❌ Error creating India border mask:', details);
    }
  }, [indiaGeoJSON]);

  return null;
}

export default IndiaBorderMask;